// Landed Costing (as v2.0.41): extra charges for a PO (or one of its shipments), each converted to the PO currency
// with a manual rate, are spread over the PO lines by goods value or quantity. Landed total = goods value + the
// charges spread onto the line. A Final costing needs review when the PO value or revision changes afterwards.

export const CHARGE_CATEGORIES = ["Freight", "Duty / Customs", "Clearance", "Insurance", "Inland Transport", "Bank / Finance Charges", "Inspection / Testing", "Other"] as const;
export const CHARGE_CURRENCIES = ["USD", "GBP", "EUR", "SAR", "AED", "PKR", "CNY", "BDT", "INR", "TRY"] as const;
export type LandedStatus = "Not Started" | "Draft" | "Final" | "Needs Review";

export interface Charge { id: string; category: string; description: string; shipmentId: string; currency: string; amount: number; rate: number }
/** A PO line for costing: quantity and goods value; `shipments` is what each shipment carries of it. */
/** `qty` is the pieces the line really brings (shipped + still open); `ordered` is the PO quantity, for reference. */
export interface CostLine { lineId: string; code: string; description: string; colour: string; unit: string; qty: number; ordered?: number; unitPrice: number; shipments: Record<string, number> }

const r2 = (n: number) => Math.round(n * 100) / 100;
let seq = 0;
export function normalizeCharge(c: any = {}, poCurrency = "USD"): Charge {
  const currency = (CHARGE_CURRENCIES as readonly string[]).includes(c.currency) ? c.currency : poCurrency;
  const rate = currency === poCurrency ? 1 : Number(c.rate) > 0 ? Math.round(Number(c.rate) * 1e6) / 1e6 : 1;
  return {
    id: String(c.id || `c${Date.now().toString(36)}${(seq++).toString(36)}`).slice(0, 40),
    category: (CHARGE_CATEGORIES as readonly string[]).includes(c.category) ? c.category : "Freight",
    description: String(c.description || "").slice(0, 300), shipmentId: String(c.shipmentId || ""), currency,
    amount: r2(Math.max(0, Number(c.amount) || 0)), rate,
  };
}
/** The charge in the PO currency. */
export const chargeValue = (c: Charge) => r2(c.amount * c.rate);

export function allocate(lines: CostLine[], charges: Charge[], method: "value" | "quantity") {
  const out = lines.map((l) => ({ ...l, goods: r2(l.qty * l.unitPrice), charges: 0 }));
  for (const c of charges) {
    const v = c.amount * c.rate;
    if (!v) continue;
    // A shipment charge goes onto what that shipment carries; a PO-wide charge onto every line.
    const basis = out.map((l) => {
      const qty = c.shipmentId ? Number(l.shipments[c.shipmentId]) || 0 : l.qty;
      return { l, qty, val: qty * l.unitPrice };
    }).filter((b) => b.qty > 0);
    let key: "qty" | "val" = method === "quantity" ? "qty" : "val";
    let total = basis.reduce((a, b) => a + b[key], 0);
    if (total <= 0) { key = "qty"; total = basis.reduce((a, b) => a + b.qty, 0); }
    if (total <= 0) continue;
    for (const b of basis) b.l.charges += v * (b[key] / total);
  }
  return out.map((l) => {
    const charges = r2(l.charges), landed = r2(l.goods + l.charges);
    return { ...l, charges, landed, landedUnit: l.qty ? Math.round((landed / l.qty) * 10000) / 10000 : 0, upliftPct: l.goods ? Math.round((l.charges / l.goods) * 10000) / 100 : 0 };
  });
}
export function costTotals(rows: ReturnType<typeof allocate>, charges: Charge[]) {
  const goods = r2(rows.reduce((a, l) => a + l.goods, 0)), allocated = r2(rows.reduce((a, l) => a + l.charges, 0)), qty = rows.reduce((a, l) => a + l.qty, 0);
  const extra = r2(charges.reduce((a, c) => a + c.amount * c.rate, 0));
  return { goods, charges: extra, allocated, landed: r2(goods + allocated), qty, avgUnit: qty ? Math.round(((goods + allocated) / qty) * 10000) / 10000 : 0, upliftPct: goods ? Math.round((allocated / goods) * 10000) / 100 : 0 };
}
/** Final costings need review when the PO's value or revision changed after they were finalized. */
export function landedStatus(c: { status: string; goodsSnapshot: number | null; revisionSnapshot: number | null } | null, goodsNow: number, revisionNow: number): LandedStatus {
  if (!c) return "Not Started";
  if (c.status !== "final") return "Draft";
  if (c.goodsSnapshot !== null && Math.abs(c.goodsSnapshot - goodsNow) > 0.005) return "Needs Review";
  if (c.revisionSnapshot !== null && c.revisionSnapshot !== revisionNow) return "Needs Review";
  return "Final";
}
